import { memo, useCallback, useMemo, useRef } from "react"

import usePermissions from "api/usePermissions"
import { permissionsUtil } from "utils/permissionsUtil"
import { getSelectProjectOptions } from "utils/projectsUtil"

import { FilterPopover } from "components/FilterComponents/FilterPopover/FilterPopover"
import { FilterType } from "components/FilterComponents/FilterPopover/types"

import { getProjectsFromStorage } from "../../utils"

interface ProjectFilterProps {
    selectedProjects: string[]
    setSelectedProjects: (projects: string[]) => void
}

const ALL_PROJECTS_VALUE = '__ALL__';

export const ProjectFilter = memo(({ selectedProjects, setSelectedProjects }: ProjectFilterProps) => {
    const { getPermissions } = usePermissions();
    const permissions = getPermissions();
    const allowedPublishers = useMemo(() => permissionsUtil.getAccessPublisherOrders(permissions), [permissions]);

    const projectsDetails = useMemo(() => {
        return getProjectsFromStorage()
    }, [])

    const projectOptions = useMemo(() => {
        const options = projectsDetails && allowedPublishers.length > 0 ? getSelectProjectOptions(projectsDetails, allowedPublishers).map(project => ({
            label: project.content,
            value: project.value
        }))
            : []

        // Add "All" option at the beginning
        return [
            { label: 'All', value: ALL_PROJECTS_VALUE },
            ...options
        ]
    }, [projectsDetails, allowedPublishers]);

    // Get all project values (excluding "All")
    const allProjectValues = useMemo(() => {
        return projectOptions.filter(opt => opt.value !== ALL_PROJECTS_VALUE).map(opt => opt.value)
    }, [projectOptions])

    // Check if all projects are selected
    const areAllProjectsSelected = useMemo(() => {
        return allProjectValues.length > 0 && allProjectValues.every(val => selectedProjects.includes(val))
    }, [allProjectValues, selectedProjects])

    // Add "All" to selectedItems if all projects are selected
    const displayedSelectedProjects = useMemo(() => {
        if (areAllProjectsSelected) {
            return [ALL_PROJECTS_VALUE, ...selectedProjects]
        }
        return selectedProjects
    }, [areAllProjectsSelected, selectedProjects])

    const selectedProjectsRef = useRef(selectedProjects)
    const setSelectedProjectsRef = useRef(setSelectedProjects)
    const allProjectValuesRef = useRef(allProjectValues)

    selectedProjectsRef.current = selectedProjects
    setSelectedProjectsRef.current = setSelectedProjects
    allProjectValuesRef.current = allProjectValues

    const handleProjectChange = useCallback((value: string) => {
        const currentProjects = selectedProjectsRef.current
        const allValues = allProjectValuesRef.current

        // Handle "All" option
        if (value === ALL_PROJECTS_VALUE) {
            // Check if all are currently selected
            const areAllSelected = allValues.every(val => currentProjects.includes(val))

            if (areAllSelected) {
                // Deselect all
                setSelectedProjectsRef.current([])
            } else {
                // Select all
                setSelectedProjectsRef.current([...allValues])
            }
        } else {
            // Handle individual project selection
            const newSelectedProjects = currentProjects.includes(value)
                ? currentProjects.filter(p => p !== value)
                : [...currentProjects, value];

            setSelectedProjectsRef.current(newSelectedProjects);
        }
    }, []);

    return <FilterPopover label="Project" options={projectOptions} selectedItems={displayedSelectedProjects} onChange={handleProjectChange} filterType={FilterType.AUTOCOMPLETE_CHECKLIST} placeholder="Search projects..." />;
});

ProjectFilter.displayName = 'ProjectFilter';